import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * The Liftline mark: a garage outline with its door lifted up under the roof
 * and the "lift line" across the threshold. Keep these coordinates in sync with
 * public/images/brand/favicon.svg and src/pages/api/brand/icon.tsx.
 */
export const LOGO_PATHS = {
    frame: 'M9.5 30.5V17.4L20 9.6l10.5 7.8v13.1',
    slats: [
        { x: 13.4, y: 17.4, width: 13.2, height: 2.4 },
        { x: 13.4, y: 21, width: 13.2, height: 2.4 }
    ],
    line: { x: 13.4, y: 27.4, width: 13.2, height: 2.8 }
}

export const LOGO_COLORS = {
    ink: '#15171a',
    cream: '#f4f1ea',
    accentOnDark: '#5b86b5',
    accentOnLight: '#1e3a5c'
}

interface LogoMarkProps {
    className?: string
    inverted?: boolean
}

export const LogoMark: FC<LogoMarkProps> = ({ className, inverted = false }): ReactNode => {
    const background = inverted ? LOGO_COLORS.cream : LOGO_COLORS.ink
    const foreground = inverted ? LOGO_COLORS.ink : LOGO_COLORS.cream
    const accent = inverted ? LOGO_COLORS.accentOnLight : LOGO_COLORS.accentOnDark

    return (
        <svg viewBox='0 0 40 40' className={cn('shrink-0', className)} aria-hidden='true'>
            <rect width='40' height='40' rx='11' fill={background} />

            <path
                d={LOGO_PATHS.frame}
                fill='none'
                stroke={foreground}
                strokeWidth='2.4'
                strokeLinecap='round'
                strokeLinejoin='round'
            />

            <rect {...LOGO_PATHS.slats[0]} rx='0.9' fill={foreground} />
            <rect {...LOGO_PATHS.slats[1]} rx='0.9' fill={foreground} opacity='0.5' />
            <rect {...LOGO_PATHS.line} rx='1.4' fill={accent} />
        </svg>
    )
}

interface LogoProps {
    className?: string
    tone?: 'dark' | 'light'
}

export const LOGO_SRC = '/images/brand/sysafrica-logo.png'

export const Logo: FC<LogoProps> = ({ className, tone = 'dark' }): ReactNode => {
    const onDark = tone === 'light'

    return (
        <span
            className={cn(
                'inline-flex items-center',
                onDark && 'bg-white px-2 py-1',
                className
            )}
        >
            <img
                src={LOGO_SRC}
                alt="SYSAFRICA"
                width={1160}
                height={870}
                className={cn('w-auto', onDark ? 'h-12' : 'h-14')}
            />
        </span>
    )
}
